import {
  CheckCircle2,
  XCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  Database,
  Users,
} from "lucide-react";

export default function ReconciliationOverview({ stats }) {
  const hasClickHandler = typeof stats?.handleSummaryClick === "function";

  const percentage =
    stats?.total > 0
      ? ((stats.matched / stats.total) * 100).toFixed(2)
      : "0.00";

  const handleClick = (type) => {
    if (hasClickHandler) {
      stats.handleSummaryClick(type);
    }
  };

  return (
    <div className="space-y-4">
      {/* Score */}
      <div className="rounded-xl border bg-white shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Reconciliation Score
            </h2>

            <p className="text-sm text-slate-500">
              {stats.matched} of {stats.total} transactions matched
            </p>
          </div>

          <div className="text-right">
            <div className="text-3xl font-bold text-green-600">
              {percentage}%
            </div>

            <div className="text-xs uppercase tracking-wide text-slate-500">
              Match Rate
            </div>
          </div>
        </div>

        <div className="mt-3 h-2 rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-green-500"
            style={{
              width: `${percentage}%`,
            }}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Transaction */}
        <SummaryCard title="Transaction Analysis">
          <SummaryRow
            icon={<CheckCircle2 size={18} className="text-green-600" />}
            label="Matched"
            value={stats.matched}
            valueColor="text-green-600"
            clickable={hasClickHandler}
            onClick={() => handleClick("Matched")}
          />

          <SummaryRow
            icon={<XCircle size={18} className="text-red-600" />}
            label="Unmatched"
            value={stats.unmatched}
            valueColor="text-red-600"
            last
            clickable={hasClickHandler}
            onClick={() => handleClick("Unmatched")}
          />
        </SummaryCard>

        {/* Missing Transactions */}
        <SummaryCard title="Missing Transactions">
          <SummaryRow
            icon={<ArrowUpCircle size={18} className="text-orange-500" />}
            label="Source Only"
            value={stats.sourceOnly}
            valueColor="text-orange-600"
            clickable={hasClickHandler}
            onClick={() => handleClick("Source Only")}
          />

          <SummaryRow
            icon={<ArrowDownCircle size={18} className="text-blue-600" />}
            label="Target Only"
            value={stats.targetOnly}
            valueColor="text-blue-600"
            last
            clickable={hasClickHandler}
            onClick={() => handleClick("Target Only")}
          />
        </SummaryCard>

        {/* Source */}
        <SummaryCard title="Source vs Target">
          <CompareRow
            icon={<Database size={18} className="text-indigo-600" />}
            label="Records"
            source={stats.sourceRecords}
            target={stats.targetRecords}
            clickable={hasClickHandler}
            onClick={() => handleClick("Unmatched")}
          />

          <CompareRow
            icon={<Users size={18} className="text-violet-600" />}
            label="Payment Parties"
            source={stats.sourceParty}
            target={stats.targetParty}
            last
            clickable={hasClickHandler}
            onClick={() => handleClick("Unmatched")}
          />
        </SummaryCard>
      </div>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div className="rounded-xl border bg-white shadow-sm">
      <div className="px-4 py-3 border-b">
        <h3 className="font-semibold text-slate-800">
          {title}
        </h3>
      </div>

      <div>{children}</div>
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  valueColor = "",
  last = false,
  onClick,
  clickable = false,
}) {
  const content = (
    <>
      <div className="flex items-center gap-3">
        <div
          className={
            clickable
              ? "group-hover:scale-110 transition-transform"
              : ""
          }
        >
          {icon}
        </div>

        <span className="font-medium text-slate-700">
          {label}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className={`text-2xl font-bold ${valueColor}`}>
          {value}
        </span>

        {/* Only show arrow when clickable */}
        {clickable && (
          <svg
            className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        )}
      </div>
    </>
  );

  if (!clickable) {
    return (
      <div
        className={`
          w-full
          flex items-center justify-between
          px-4 py-3
          text-left
          ${!last ? "border-b" : ""}
        `}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full
        flex items-center justify-between
        px-4 py-3
        text-left
        transition-all
        hover:bg-slate-50
        hover:shadow-sm
        active:scale-[0.99]
        group
        cursor-pointer
        ${!last ? "border-b" : ""}
      `}
    >
      {content}
    </button>
  );
}

function CompareRow({
  icon,
  label,
  source,
  target,
  last = false,
  onClick,
  clickable = false,
}) {
  const content = (
    <>
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>

      <div className="text-center font-semibold">
        {source}
      </div>

      <div className="text-center font-semibold">
        {target}
      </div>

      {/* Arrow only when clickable */}
      <div className="flex justify-center">
        {clickable && (
          <svg
            className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        )}
      </div>
    </>
  );

  if (!clickable) {
    return (
      <div
        className={`
          w-full
          grid grid-cols-[1fr_70px_70px_20px]
          items-center
          px-4 py-3
          text-left
          ${!last ? "border-b" : ""}
        `}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full
        grid grid-cols-[1fr_70px_70px_20px]
        items-center
        px-4 py-3
        text-left
        hover:bg-slate-50
        transition-all
        group
        cursor-pointer
        ${!last ? "border-b" : ""}
      `}
    >
      {content}
    </button>
  );
}